import * as React from 'react';
import { sanitize } from 'dompurify';
import { useIntl } from 'react-intl-next';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { AutoScaledImage } from '@atlassian/help-center-common-component/auto-scaled-image';
import messages from './messages';
import { PortalName, Description, Root, IconContainer, Content, Container } from './styled';

interface PortalCardProps {
    id: number;
    // index of this portal in popular portal list view
    index?: number;
    portalName: string;
    description?: string;
    portalUrl: string;
    iconUrl?: string;
    onClick?: (index?: number) => void;
}

const PortalCard: React.FunctionComponent<PortalCardProps> = ({
    onClick,
    index,
    id,
    portalName,
    description,
    portalUrl,
    iconUrl,
}) => {
    const memoizedOnClick = React.useCallback(() => onClick && onClick(index), [onClick, index]);

    const hasIcon = !!iconUrl;
    const hasPortalUrl = !!portalUrl;
    const hasDescription = !!description;
    const hasOnlyPortalName = !description && !iconUrl;
    const { formatMessage } = useIntl();

    return (
        <AnalyticsContext
            data={{
                attributes: {
                    index,
                    hasIcon,
                    hasPortalUrl,
                    hasDescription,
                    portalId: id,
                },
            }}
        >
            <Root
                to={portalUrl}
                onClick={memoizedOnClick}
                actionSubjectId="portal"
                data-test-id={`portal:${portalName}`}
            >
                <Container hasIcon={hasIcon}>
                    {!!iconUrl && (
                        <IconContainer>
                            <AutoScaledImage
                                src={iconUrl}
                                alt={formatMessage(messages.portalLogoAlt, { portalName })}
                            />
                        </IconContainer>
                    )}
                    <Content>
                        <PortalName hasOnlyPortalName={hasOnlyPortalName} title={portalName}>
                            {portalName}
                        </PortalName>
                        {!!description && (
                            <Description
                                title={description}
                                dangerouslySetInnerHTML={{
                                    __html: sanitize(description),
                                }}
                            />
                        )}
                    </Content>
                </Container>
            </Root>
        </AnalyticsContext>
    );
};

export default PortalCard;
